import { Suspense, lazy, useEffect, useMemo, useState } from 'react';
import {
  Navigate,
  Route,
  Routes,
  useLocation,
  useNavigate,
  useParams,
  type Location,
  useSearchParams,
} from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from './context/AuthContext';
import { BottomNav } from './components/layout/BottomNav';
import { DesktopNavbar } from './components/layout/DesktopNavbar';
import { MobileHomeTop } from './components/home/MobileHomeTop';
import { EmptyState } from './components/ui/EmptyState';
import { LoadingSpinner } from './components/ui/LoadingSpinner';
import { SplashScreen } from './components/onboarding/SplashScreen';
import { OnboardingPage, isOnboardingDone } from './pages/OnboardingPage';
import { ONBOARDING_SLIDES, SPLASH_IMAGE_URL } from './constants/onboarding';
import { preloadImages } from './utils/preloadImages';
import { useIsDesktop } from './hooks/useMediaQuery';
import { RequireAuth } from './components/auth/RequireAuth';
import { getWalletBackground } from './navigation/openWallet';
import { Page } from './components/layout/Page';
import { SupportChatFab } from './components/support/SupportChatFab';

const HomePage = lazy(() => import('./pages/HomePage').then((m) => ({ default: m.HomePage })));
const ExplorePage = lazy(() => import('./pages/ExplorePage').then((m) => ({ default: m.ExplorePage })));
const CountryPlansPage = lazy(() =>
  import('./pages/CountryPlansPage').then((m) => ({ default: m.CountryPlansPage })),
);
const CountryPlanReviewPage = lazy(() =>
  import('./pages/CountryPlanReviewPage').then((m) => ({ default: m.CountryPlanReviewPage })),
);
const PlanDetailPage = lazy(() => import('./pages/PlanDetailPage').then((m) => ({ default: m.PlanDetailPage })));
const CheckoutPage = lazy(() => import('./pages/CheckoutPage').then((m) => ({ default: m.CheckoutPage })));
const OrderProcessingPage = lazy(() =>
  import('./pages/OrderProcessingPage').then((m) => ({ default: m.OrderProcessingPage })),
);
const OrderSuccessPage = lazy(() =>
  import('./pages/OrderSuccessPage').then((m) => ({ default: m.OrderSuccessPage })),
);
const OrderPage = lazy(() => import('./pages/OrderPage').then((m) => ({ default: m.OrderPage })));
const OrderDetailPage = lazy(() =>
  import('./pages/OrderDetailPage').then((m) => ({ default: m.OrderDetailPage })),
);
const MyEsimsPage = lazy(() => import('./pages/MyEsimsPage').then((m) => ({ default: m.MyEsimsPage })));
const EsimDetailPage = lazy(() => import('./pages/EsimDetailPage').then((m) => ({ default: m.EsimDetailPage })));
const EsimInstallPage = lazy(() =>
  import('./pages/EsimInstallPage').then((m) => ({ default: m.EsimInstallPage })),
);
const EsimUsagePage = lazy(() => import('./pages/EsimUsagePage').then((m) => ({ default: m.EsimUsagePage })));
const FavoritesPage = lazy(() => import('./pages/FavoritesPage').then((m) => ({ default: m.FavoritesPage })));
const ProfilePage = lazy(() => import('./pages/ProfilePage').then((m) => ({ default: m.ProfilePage })));
const SettingsPage = lazy(() => import('./pages/SettingsPage').then((m) => ({ default: m.SettingsPage })));
const EditProfilePage = lazy(() =>
  import('./pages/EditProfilePage').then((m) => ({ default: m.EditProfilePage })),
);
const PrivacyPage = lazy(() => import('./pages/PrivacyPage').then((m) => ({ default: m.PrivacyPage })));
const LegalPage = lazy(() => import('./pages/LegalPage').then((m) => ({ default: m.LegalPage })));
const VerifyMethodPage = lazy(() =>
  import('./pages/verify/VerifyMethodPage').then((m) => ({ default: m.VerifyMethodPage })),
);
const VerifyPinPage = lazy(() =>
  import('./pages/verify/VerifyPinPage').then((m) => ({ default: m.VerifyPinPage })),
);
const VerifySuccessPage = lazy(() =>
  import('./pages/verify/VerifySuccessPage').then((m) => ({ default: m.VerifySuccessPage })),
);
const VerifyEmailPage = lazy(() =>
  import('./pages/VerifyEmailPage').then((m) => ({ default: m.VerifyEmailPage })),
);
const SupportPage = lazy(() => import('./pages/SupportPage').then((m) => ({ default: m.SupportPage })));
const SupportChatPage = lazy(() =>
  import('./pages/SupportChatPage').then((m) => ({ default: m.SupportChatPage })),
);
const InvitePage = lazy(() => import('./pages/InvitePage').then((m) => ({ default: m.InvitePage })));
const TopupPage = lazy(() => import('./pages/TopupPage').then((m) => ({ default: m.TopupPage })));
const WalletHistoryPage = lazy(() =>
  import('./pages/WalletHistoryPage').then((m) => ({ default: m.WalletHistoryPage })),
);
const PaymentMethodsPage = lazy(() =>
  import('./pages/PaymentMethodsPage').then((m) => ({ default: m.PaymentMethodsPage })),
);
const NotificationsPage = lazy(() =>
  import('./pages/NotificationsPage').then((m) => ({ default: m.NotificationsPage })),
);
const SomethingWentWrongPage = lazy(() =>
  import('./pages/SomethingWentWrongPage').then((m) => ({ default: m.SomethingWentWrongPage })),
);
const WalletCheckoutPage = lazy(() =>
  import('./pages/WalletCheckoutPage').then((m) => ({ default: m.WalletCheckoutPage })),
);
const LoginPage = lazy(() => import('./pages/LoginPage').then((m) => ({ default: m.LoginPage })));
const RegisterPage = lazy(() => import('./pages/RegisterPage').then((m) => ({ default: m.RegisterPage })));
const ForgotPasswordPage = lazy(() =>
  import('./pages/ForgotPasswordPage').then((m) => ({ default: m.ForgotPasswordPage })),
);
const ResetPasswordPage = lazy(() =>
  import('./pages/ResetPasswordPage').then((m) => ({ default: m.ResetPasswordPage })),
);

const TAB_ROOTS = ['/', '/explore', '/my-esims', '/profile'];
const AUTH_PAGES = [
  '/login',
  '/register',
  '/onboarding',
  '/forgot-password',
  '/reset-password',
  '/legal',
  '/legal/terms',
  '/legal/privacy',
  '/verify-email',
  '/verify',
  '/verify/pin',
  '/verify/success',
];

function EsimQrRedirect() {
  const { id } = useParams<{ id: string }>();
  return <Navigate to={`/esim/${id ?? ''}`} replace />;
}

function CountrySelectRedirect() {
  const { code } = useParams<{ code: string }>();
  const [params] = useSearchParams();
  const countryCode = code?.toUpperCase() || '';
  const plan = params.get('plan');
  const highlight = params.get('highlight');
  const days = params.get('days');
  if (plan) {
    const query = new URLSearchParams({ plan });
    if (days) query.set('days', days);
    return <Navigate to={`/country/${countryCode}/review?${query.toString()}`} replace />;
  }
  if (highlight) {
    const query = new URLSearchParams({ highlight });
    if (days) query.set('days', days);
    return <Navigate to={`/country/${countryCode}?${query.toString()}`} replace />;
  }
  const leftover = params.toString();
  return <Navigate to={leftover ? `/country/${countryCode}?${leftover}` : `/country/${countryCode}`} replace />;
}

function isTabRoot(pathname: string) {
  return TAB_ROOTS.includes(pathname);
}

function RouteFallback() {
  return (
    <Page>
      <LoadingSpinner />
    </Page>
  );
}

function AppRoutes({ routesLocation }: { routesLocation?: Location }) {
  const navigate = useNavigate();
  return (
    <Suspense fallback={<RouteFallback />}>
      <Routes location={routesLocation}>
        <Route path="/onboarding" element={<OnboardingPage onComplete={() => navigate('/', { replace: true })} />} />
        <Route path="/login" element={<LoginPage />} />
        <Route path="/register" element={<RegisterPage />} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />
        <Route path="/legal" element={<LegalPage />} />
        <Route path="/legal/terms" element={<LegalPage />} />
        <Route path="/legal/privacy" element={<LegalPage />} />
        <Route path="/verify-email" element={<VerifyEmailPage />} />
        <Route path="/verify" element={<VerifyMethodPage />} />
        <Route path="/verify/pin" element={<VerifyPinPage />} />
        <Route path="/verify/success" element={<VerifySuccessPage />} />
        <Route path="/" element={<HomePage />} />
        <Route path="/explore" element={<ExplorePage />} />
        <Route path="/country/:code" element={<CountryPlansPage />} />
        <Route path="/country/:code/select" element={<CountrySelectRedirect />} />
        <Route path="/country/:code/review" element={<CountryPlanReviewPage />} />
        <Route path="/plan/:id" element={<PlanDetailPage />} />
        <Route path="/support" element={<SupportPage />} />
        <Route path="/support/chat" element={<RequireAuth><SupportChatPage /></RequireAuth>} />
        <Route path="/something_went_wrong" element={<SomethingWentWrongPage />} />

        <Route path="/checkout/:id" element={<RequireAuth><CheckoutPage /></RequireAuth>} />
        <Route path="/order/:id/processing" element={<RequireAuth><OrderProcessingPage /></RequireAuth>} />
        <Route path="/order/:id/success" element={<RequireAuth><OrderSuccessPage /></RequireAuth>} />
        <Route path="/order/:id" element={<RequireAuth><OrderPage /></RequireAuth>} />
        <Route path="/orders" element={<RequireAuth><Navigate to="/wallet/history" replace /></RequireAuth>} />
        <Route path="/orders/:id" element={<RequireAuth><OrderDetailPage /></RequireAuth>} />
        <Route path="/wallet/history" element={<RequireAuth><WalletHistoryPage /></RequireAuth>} />
        <Route path="/wallet/payment-methods" element={<RequireAuth><PaymentMethodsPage /></RequireAuth>} />
        <Route path="/notifications" element={<RequireAuth><NotificationsPage /></RequireAuth>} />
        <Route path="/favorites" element={<FavoritesPage />} />
        <Route path="/my-esims" element={<RequireAuth><MyEsimsPage /></RequireAuth>} />
        <Route path="/esim/:id" element={<RequireAuth><EsimDetailPage /></RequireAuth>} />
        <Route path="/esim/:id/qr" element={<RequireAuth><EsimQrRedirect /></RequireAuth>} />
        <Route path="/esim/:id/install" element={<RequireAuth><EsimInstallPage /></RequireAuth>} />
        <Route path="/esim/:id/usage" element={<RequireAuth><EsimUsagePage /></RequireAuth>} />
        <Route path="/profile" element={<ProfilePage />} />
        <Route path="/invite" element={<RequireAuth><InvitePage /></RequireAuth>} />
        <Route path="/settings" element={<RequireAuth><SettingsPage /></RequireAuth>} />
        <Route path="/settings/edit-profile" element={<RequireAuth><EditProfilePage /></RequireAuth>} />
        <Route path="/settings/privacy" element={<RequireAuth><PrivacyPage /></RequireAuth>} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Suspense>
  );
}

function Layout() {
  const location = useLocation();
  const navigate = useNavigate();
  const { mode, isAuthenticated } = useAuth();
  const isDesktop = useIsDesktop();
  const isAuthPage = AUTH_PAGES.includes(location.pathname);
  const isWalletPath = location.pathname === '/topup';
  // Guests must not enter the wallet shell — it dims the page (pointer-events: none)
  // and used to stack a second auth gate under/over the background route.
  const showWalletModal = isWalletPath && (mode === 'telegram' || isAuthenticated);
  const background = getWalletBackground(location);

  const routesLocation = useMemo(() => {
    if (!showWalletModal) return location;
    if (background) return background;
    // Direct /topup link: keep home mounted under the sheet
    return {
      ...location,
      pathname: '/',
      search: '',
      hash: '',
      state: null,
    } as Location;
  }, [showWalletModal, background, location]);

  const chromePath = routesLocation.pathname;
  const showMobileChrome = !isDesktop;
  const isCheckoutFlow = /^\/country\/[^/]+\/(select|review)$/.test(chromePath);
  const isPlanSelect = showMobileChrome && isCheckoutFlow;
  const isOrderSuccess = /^\/order\/[^/]+\/success$/.test(chromePath);
  const isEsimDetail = /^\/esim\/[^/]+$/.test(chromePath);
  const isSupportChat = chromePath === '/support/chat';
  const showBottomNav = showMobileChrome && !showWalletModal && !isSupportChat && !isOrderSuccess;
  const showMobileTop = showMobileChrome && !isPlanSelect && !isSupportChat && !isOrderSuccess && !isEsimDetail;
  const showSupportFab = !isSupportChat && !showWalletModal && !isPlanSelect && !isOrderSuccess && !isEsimDetail;

  // Guest hit /topup directly → real login page, not a broken in-shell auth gate.
  useEffect(() => {
    if (!isWalletPath || showWalletModal || mode === 'telegram') return;
    const search = location.search || '';
    const returnTo = `/topup${search}`;
    navigate(`/login?returnTo=${encodeURIComponent(returnTo)}`, { replace: true });
  }, [isWalletPath, showWalletModal, mode, location.search, navigate]);

  // `.cs-desk-shell--wallet-open` only stops scrolling of its own internal
  // scroll container — on mobile the page scrolls at the document level, and
  // plain `overflow: hidden` on body/html famously doesn't block touch-driven
  // scrolling in iOS Safari / Telegram's in-app browser. Freezing the body in
  // place with `position: fixed` (and restoring the exact scroll offset after)
  // is the reliable cross-browser way to lock it.
  //
  // Exception: Telegram support chat — body lock fights the WebView keyboard /
  // visualViewport math and makes the composer lag or stick under the keyboard.
  // The chat shell is already position:fixed + overflow:hidden.
  useEffect(() => {
    if (!showWalletModal && !isSupportChat) return;
    if (mode === 'telegram' && isSupportChat && !showWalletModal) return;

    const { body } = document;
    const scrollY = window.scrollY;
    const prevPosition = body.style.position;
    const prevTop = body.style.top;
    const prevWidth = body.style.width;
    const prevOverflow = body.style.overflow;

    body.style.position = 'fixed';
    body.style.top = `-${scrollY}px`;
    body.style.width = '100%';
    body.style.overflow = 'hidden';

    return () => {
      body.style.position = prevPosition;
      body.style.top = prevTop;
      body.style.width = prevWidth;
      body.style.overflow = prevOverflow;
      window.scrollTo(0, scrollY);
    };
  }, [showWalletModal, isSupportChat, mode]);

  useEffect(() => {
    if (mode !== 'telegram') return;
    const tg = window.Telegram?.WebApp;
    const back = tg?.BackButton;
    if (!back) return;

    if (showWalletModal) {
      back.show();
      const handler = () => navigate(-1);
      back.onClick(handler);
      return () => {
        back.offClick(handler);
        back.hide();
      };
    }

    if (isOrderSuccess) {
      back.show();
      const handler = () => navigate('/my-esims', { replace: true });
      back.onClick(handler);
      return () => {
        back.offClick(handler);
        back.hide();
      };
    }

    const showBack =
      showMobileChrome &&
      !isTabRoot(chromePath) &&
      chromePath !== '/onboarding';
    if (showBack) {
      back.show();
      const handler = () => navigate(-1);
      back.onClick(handler);
      return () => {
        back.offClick(handler);
        back.hide();
      };
    }
    back.hide();
  }, [chromePath, showMobileChrome, showWalletModal, navigate, mode, isOrderSuccess]);

  if (isAuthPage) {
    return <AppRoutes />;
  }

  return (
    <div className={`cs-desk-shell${isDesktop ? '' : ' cs-desk-shell--compact'}${showWalletModal ? ' cs-desk-shell--wallet-open' : ''}${isPlanSelect ? ' cs-desk-shell--plan-select' : ''}${isSupportChat ? ' cs-desk-shell--chat' : ''}${isOrderSuccess ? ' cs-desk-shell--order-ready' : ''}`}>
      {isDesktop && !isOrderSuccess ? <DesktopNavbar /> : showMobileTop ? <MobileHomeTop /> : null}
      <main className="cs-desk-shell__main">
        <AppRoutes routesLocation={routesLocation} />
      </main>
      {showWalletModal && (
        <Suspense fallback={null}>
          <TopupPage />
        </Suspense>
      )}
      {showSupportFab ? <SupportChatFab /> : null}
      {showBottomNav && <BottomNav />}
    </div>
  );
}

export function App() {
  const location = useLocation();
  const { ready, error, token, mode } = useAuth();
  const { t } = useTranslation();
  const [showOnboarding, setShowOnboarding] = useState(() => !isOnboardingDone());
  const [bootstrapped, setBootstrapped] = useState(false);
  const [splashProgress, setSplashProgress] = useState(0);

  useEffect(() => {
    // /pay is a session-less Stripe wallet page — skip splash asset warming entirely.
    if (location.pathname === '/pay') {
      setBootstrapped(true);
      return;
    }
    if (!ready || bootstrapped) return;

    let cancelled = false;

    // Warm the real LCP image during splash (onboarding for first visit, else home hero).
    const warm = showOnboarding
      ? [SPLASH_IMAGE_URL, ONBOARDING_SLIDES[0].illustration ?? '/onboarding/hero-1.webp']
      : [SPLASH_IMAGE_URL, '/onboarding/hero-1-sm.webp'];
    preloadImages(warm, (percent) => {
      if (!cancelled) setSplashProgress(percent);
    }).then(() => {
      if (!cancelled) {
        setSplashProgress(100);
        setBootstrapped(true);
      }
    });

    return () => {
      cancelled = true;
    };
  }, [ready, bootstrapped, showOnboarding, location.pathname]);

  // Standalone Apple Pay/Google Pay checkout — opened as a fresh external browser tab, no
  // Telegram context and no user session. Bypasses the splash/auth/onboarding gates below
  // (which assume either a Telegram-mode bootstrap or an existing web-mode login) entirely;
  // the Stripe clientSecret in its own URL is the only credential this page needs. This check
  // comes after all hooks above (rules-of-hooks: an early return must never skip a hook call)
  // but still before every gate that would otherwise block a fresh, session-less browser tab.
  if (location.pathname === '/pay') {
    return (
      <Suspense fallback={<RouteFallback />}>
        <WalletCheckoutPage />
      </Suspense>
    );
  }

  if (!ready || !bootstrapped) {
    return <SplashScreen progress={splashProgress} />;
  }

  if (mode === 'telegram' && (error || !token)) {
    return (
      <EmptyState
        icon="🔒"
        title="CozzySIM"
        description={error || t('auth.accessDenied')}
      />
    );
  }

  if (showOnboarding) {
    return <OnboardingPage onComplete={() => setShowOnboarding(false)} />;
  }

  return <Layout />;
}
